import {memo, useMemo} from 'react'
import {compact} from 'lodash-es'
import ResponsiveDialog from '~/components/common/ResponsiveDialog'
import SizeTable from '~/components/product-details/SizeTable'
import SizeImage from '~/components/product-details/SizeImage'
import {filterProductVariants} from '~/utils/product'
import {Product, ProductSize} from '~/gql/graphql'
import {useTranslations} from 'use-intl'

type SizeDialogProps = {
    onClose: () => void
    product: Product
    selectedSize?: ProductSize
    open: boolean
}

const SizeDialog = memo(({onClose, product, selectedSize, open}: SizeDialogProps) => {
    const t = useTranslations('productDetails.sizeDialog')

    const sizes = useMemo(() => {
        const variants = compact(product.variants)
        const firstAppearanceId = variants[0].appearance?.id!

        return compact(
            filterProductVariants(variants, {appearanceId: firstAppearanceId}).map(
                (variant) => variant.size
            )
        )
    }, [product.variants])

    return (
        <ResponsiveDialog
            open={open}
            onOpenChange={onClose}
            title={t('title')}
            description={product.type?.name}
            ssr
        >
            <div className="pb-8 px-4 overflow-y-auto md:p-0">
                <div className="mb-4 rounded-lg overflow-hidden shadow-md shadow-gray-600 bg-white">
                    <SizeImage
                        className="mx-auto max-h-60 p-4 pointer-events-none select-none"
                        productTypeId={product.type?.id!}
                    />
                </div>
                <div className="space-y-2">
                    {!!product.type?.sizeFitHint && (
                        <p className="font-medium text-sm">
                            {t('fit', {fitHint: product.type.sizeFitHint})}
                        </p>
                    )}
                    <SizeTable
                        className="shadow-md shadow-gray-600"
                        sizes={sizes}
                        selectedSize={selectedSize}
                    />
                </div>
            </div>
        </ResponsiveDialog>
    )
})

export default SizeDialog
